import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon, TruckIcon, StarIcon, CalendarIcon, PlusIcon, MinusIcon, GiftIcon } from 'lucide-react';
const NAVY = '#142D52';
const CORAL = '#F05B56';
const TEAL = '#00AFAA';
const MINT = '#F0FAF9';
const sizes = ['XS', 'S', 'M', 'L', 'XL'];
const heroBenefits = ['Geen druk op keel, gewrichten of luchtpijp zodat je hond zonder stress of irritatie kan wandelen', 'Zacht materiaal - voorkomt schuurplekken voor maximaal comfort', '3 meter lijn - geeft je hond ruimte om te snuffelen terwijl die in contact blijft met zijn baasje', 'Reflecterend materiaal - voor extra veiligheid in het donker'];
const lessons = ['Les 1 - Wat een lijn kan doen', 'Les 2 - Basis lijnvoeringstechnieken', 'Les 3 - Gevorderden: wachten voor het oversteken', 'Les 4 - Gevorderden: van richting veranderen', 'Les 5 - Gevorderden: begrenzen met de lijn'];
const sizeTable = [{
  maat: 'XS',
  borst: '28 - 38 cm',
  hals: '23 - 33 cm',
  band: '2,0 cm'
}, {
  maat: 'S',
  borst: '36 - 46 cm',
  hals: '28 - 38 cm',
  band: '2,0 cm'
}, {
  maat: 'M',
  borst: '46 - 60 cm',
  hals: '36 - 50 cm',
  band: '2,5 cm'
}, {
  maat: 'L',
  borst: '56 - 74 cm',
  hals: '42 - 62 cm',
  band: '2,5 cm'
}, {
  maat: 'XL',
  borst: '68 - 92 cm',
  hals: '52 - 74 cm',
  band: '2,5 cm'
}];
const faqs = [{
  q: 'Hoe weet ik welke maat mijn hond nodig heeft?',
  a: 'Meet de borstomvang van je hond vlak achter de voorpoten met een meetlint. Vergelijk de maat met onze maattabel hierboven. Twijfel je tussen twee maten? Kies de grotere maat.'
}, {
  q: 'Wanneer ontvang ik mijn bestelling?',
  a: 'De verwachte levering is half juli 2026. Je ontvangt een mail zodra je bestelling onderweg is.'
}, {
  q: 'Kan ik mijn bestelling annuleren?',
  a: 'Ja, annuleren kan kosteloos tot aan het moment van verzending.'
}, {
  q: 'Kan ik het harnas retourneren?',
  a: 'Ja, retourneren is mogelijk op eigen kosten.'
}, {
  q: 'Waarom is de lijn 3 meter?',
  a: 'Een kortere lijn beperkt je hond in zijn natuurlijke beweging. Met 3 meter heeft je hond de ruimte om te snuffelen en te ontspannen, terwijl je in contact blijft. Dit vermindert spanning aan de lijn.'
}, {
  q: 'Is het harnas geschikt voor mijn type hond?',
  a: 'Ja. Het harnas is ontworpen voor iedere hond - van kleine honden zoals een Teckel of Boston Terrier tot grote honden. Door de verstelbare hals- en buikband past het zich aan elk hondenlichaam aan.'
}];
const ImagePlaceholder = ({
  label,
  className = '',
  rounded = 'rounded-2xl',
  aspect = 'aspect-[4/3]'
}: {
  label: string;
  className?: string;
  rounded?: string;
  aspect?: string;
}) => <div className={`w-full ${aspect} ${rounded} flex items-center justify-center text-center px-6 ${className}`} style={{
  backgroundColor: MINT,
  color: NAVY
}} data-id="element-2423">
    <span className="text-sm font-medium opacity-70" data-id="element-2424">{label}</span>
  </div>;
const CHECKOUT_URL = 'https://afrekenen.stresslessdogs.nl/checkout/stressless-hondenharnas-bundel';
const CTAButton = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <a href={CHECKOUT_URL} className={`block w-full text-center uppercase font-bold tracking-wide text-white py-4 px-6 text-base md:text-lg shadow-[0_6px_20px_rgba(240,91,86,0.35)] hover:opacity-95 transition-opacity ${className}`} style={{
  backgroundColor: CORAL,
  borderRadius: 100
}} data-id="element-2425">
    {children}
  </a>;
const Check = () => <CheckIcon className="w-5 h-5 mt-1 flex-shrink-0" style={{
  color: TEAL
}} strokeWidth={3} data-id="element-2426" />;
const FAQItem = ({
  q,
  a
}: {
  q: string;
  a: string;
}) => {
  const [open, setOpen] = useState(false);
  return <div className="bg-white rounded-2xl" style={{
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
  }} data-id="element-2427">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left p-5 md:p-6 gap-4" data-id="element-2428">
        <span className="font-bold text-base md:text-lg" style={{
        color: NAVY
      }} data-id="element-2429">
          {q}
        </span>
        {open ? <MinusIcon className="w-5 h-5 flex-shrink-0" style={{
        color: TEAL
      }} data-id="element-2430" /> : <PlusIcon className="w-5 h-5 flex-shrink-0" style={{
        color: TEAL
      }} data-id="element-2431" />}
      </button>
      {open && <div className="px-5 md:px-6 pb-5 md:pb-6 text-base leading-relaxed" style={{
      color: NAVY
    }} data-id="element-2432">
          {a}
        </div>}
    </div>;
};
export const StresslessWandelsetPage = () => {
  const [selectedSize, setSelectedSize] = useState('M');
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [heroGalleryIndex, setHeroGalleryIndex] = useState(0);
  const trustpilotRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if ((window as any).Trustpilot && trustpilotRef.current) {
      ;
      (window as any).Trustpilot.loadFromElement(trustpilotRef.current, true);
    }
  }, []);
  const heroGalleryImages = [{
    src: 'https://cdn.magicpatterns.com/uploads/3GxSvHe5GGkJLoMTMegaZS/553579c88fd74cfb054703feff63eb68.jpg',
    alt: 'Stressless Wandelset - teal harnas en lijn'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/dACEH2YL9Zng1UtPKuG7bW/2fec549f084158399ab49791b65ad753.jpg',
    alt: 'Stressless Hondenharnas - zijaanzicht'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/o6CfQYvcXGtkiB8cu9TStf/07546917680a3ddb169f8038661d5837.jpg',
    alt: 'Stressless Hondenharnas - bovenaanzicht'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/i7aQQuAasWBb7N24926kXm/72ee700432635495c54448ad503c21c0.jpg',
    alt: 'Stressless Lijn - 3 meter'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/6Ua5w6Dtccoehry9qRoaGF/1C2A3549.jpg',
    alt: 'Teckel met het Stressless Hondenharnas'
  }];
  const galleryImages = [{
    src: 'https://cdn.magicpatterns.com/uploads/dACEH2YL9Zng1UtPKuG7bW/2fec549f084158399ab49791b65ad753.jpg',
    alt: 'Stressless Hondenharnas - zijaanzicht',
    bg: 'white'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/o6CfQYvcXGtkiB8cu9TStf/07546917680a3ddb169f8038661d5837.jpg',
    alt: 'Stressless Hondenharnas - bovenaanzicht',
    bg: 'white'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/cy2Sj9JeRzFbJBaPrFD3ZG/eaf5e13a714a5407bc3e5cf1ae912d23.jpg',
    alt: 'Stressless Hondenharnas - detail',
    bg: 'white'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/i7aQQuAasWBb7N24926kXm/72ee700432635495c54448ad503c21c0.jpg',
    alt: 'Stressless Lijn - 3 meter',
    bg: 'white'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/6Ua5w6Dtccoehry9qRoaGF/1C2A3549.jpg',
    alt: 'Teckel met het Stressless Hondenharnas',
    bg: 'transparent'
  }];
  useEffect(() => {
    const handleScroll = () => {
      // Show after user scrolls past ~70% of the viewport (past hero)
      setShowStickyBar(window.scrollY > window.innerHeight * 0.7);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen bg-white pb-[80px] md:pb-0" style={{
    fontFamily: '"Sen", sans-serif',
    color: NAVY,
    fontSize: 18,
    lineHeight: 1.6
  }} data-id="element-2433">
      {/* SECTION 1: HERO */}
      {/* PART A: Intro banner */}
      <section className="py-12 md:py-20 px-4" style={{
      backgroundColor: MINT
    }} data-id="element-2434">
        <div className="max-w-[800px] mx-auto text-center" data-id="element-2435">
          <h1 className="font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.15] mb-5" style={{
          color: NAVY
        }} data-id="element-2436">
            Wandelen zoals je hond het verdient.
          </h1>
          <p className="text-lg md:text-xl mb-8 mx-auto" style={{
          color: NAVY,
          opacity: 0.85,
          maxWidth: 640
        }} data-id="element-2437">
            Het Stressless Hondenharnas & Lijn - ontwikkeld door
            gedragsdeskundigen, ontworpen voor het welzijn van je hond.
          </p>
          <img src="https://cdn.magicpatterns.com/uploads/heQVWPHhZLrbC19ho6emZY/1C2A3572.jpg" alt="Teckel met teal Stressless harnas en lijn in het park" className="w-full object-cover mx-auto" style={{
          maxWidth: 600,
          borderRadius: 16,
          aspectRatio: '4 / 3',
          boxShadow: '0 8px 28px rgba(0,0,0,0.10)'
        }} data-id="element-2438" />
        </div>
      </section>

      {/* PART B: Product detail (e-commerce layout) */}
      <section className="bg-white py-12 md:py-20 px-4" data-id="element-2439">
        <div className="max-w-6xl mx-auto grid md:grid-cols-[1.2fr_1fr] gap-10 md:gap-14 items-start" data-id="element-2440">
          {/* LEFT: Product gallery */}
          <div data-id="element-2441">
            <div className="relative mb-4" data-id="element-2442">
              <div className="w-full aspect-square overflow-hidden" style={{
              backgroundColor: MINT,
              borderRadius: 12
            }} data-id="element-2443">
                <img src={heroGalleryImages[heroGalleryIndex].src} alt={heroGalleryImages[heroGalleryIndex].alt} className="w-full h-full object-cover" data-id="element-2444" />
              </div>
            </div>
            <div className="flex justify-start gap-2 md:gap-3 flex-wrap" data-id="element-2445">
              {heroGalleryImages.map((img, i) => <button key={i} onClick={() => setHeroGalleryIndex(i)} aria-label={`Bekijk foto ${i + 1}`} className="w-14 h-14 md:w-16 md:h-16 overflow-hidden transition-all" style={{
              backgroundColor: MINT,
              borderRadius: 8,
              border: heroGalleryIndex === i ? `3px solid ${TEAL}` : '3px solid transparent',
              opacity: heroGalleryIndex === i ? 1 : 0.6
            }} data-id="element-2446">
                  <img src={img.src} alt="" className="w-full h-full object-cover" data-id="element-2447" />
                </button>)}
            </div>
          </div>

          {/* RIGHT: Product info */}
          <div data-id="element-2448">
            <h2 className="font-bold text-2xl md:text-3xl mb-1" style={{
            color: NAVY
          }} data-id="element-2449">
              Stressless Wandelset (pre-order actie)
            </h2>
            <p className="text-sm md:text-base mb-3" style={{
            color: NAVY,
            opacity: 0.65
          }} data-id="element-2450">
              Stressless Harnas + 3 mtr Lijn (+ Digitale Cursus)
            </p>

            <div className="flex items-baseline gap-3 mb-1" data-id="element-2451">
              <span className="line-through text-lg" style={{
              color: '#999999'
            }} data-id="element-2452">
                €69,90
              </span>
              <span className="font-bold text-3xl md:text-4xl" style={{
              color: CORAL
            }} data-id="element-2453">
                €44,95
              </span>
            </div>
            <div className="flex items-center gap-2 mb-2 flex-wrap" data-id="element-2454">
              <p className="text-sm" style={{
              color: NAVY,
              opacity: 0.7
            }} data-id="element-2455">
                Pre-order bundel prijs
              </p>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold" style={{
              backgroundColor: '#FDEBEA',
              color: CORAL
            }} data-id="element-2456">
                Je bespaart €24,95 (36%)
              </span>
            </div>
            <div className="flex items-center gap-2 mb-6 text-sm font-medium" style={{
            color: TEAL
          }} data-id="element-2457">
              <TruckIcon className="w-4 h-4" data-id="element-2458" />
              <span data-id="element-2459">Gratis verzending</span>
            </div>

            <ul className="space-y-3 mb-6" data-id="element-2460">
              {heroBenefits.map((b, i) => <li key={i} className="flex gap-3 items-start" data-id="element-2461">
                  <Check data-id="element-2462" />
                  <span className="text-base" style={{
                color: NAVY
              }} data-id="element-2463">
                    {b}
                  </span>
                </li>)}
            </ul>

            {/* Bonus info card */}
            <div className="rounded-2xl p-5 mb-6 text-white text-sm leading-relaxed" style={{
            backgroundColor: TEAL
          }} data-id="element-2464">
              <div className="flex items-center gap-2 mb-2 font-bold uppercase tracking-wider text-xs" data-id="element-2465">
                <StarIcon className="w-4 h-4 fill-white" data-id="element-2466" />
                Bonus: Gratis Leermodule Lijnvoering
              </div>
              Inclusief Module Lijnvoering uit de cursus "Stressless Wandelen
              met je Hond" (cursuswaarde €87). Leer direct hoe je het beste
              wandelt met een lijn van 3 meter. (Leermodule wel per direct
              beschikbaar.)
            </div>

            {/* Size selector */}
            <div className="mb-6" data-id="element-2467">
              <label className="block text-sm font-bold mb-3" style={{
              color: NAVY
            }} data-id="element-2468">
                Kies de maat
              </label>
              <div className="grid grid-cols-5 gap-2" data-id="element-2469">
                {sizes.map(s => <button key={s} onClick={() => setSelectedSize(s)} className="py-3 rounded-xl font-bold text-sm transition-all" style={{
                backgroundColor: selectedSize === s ? NAVY : MINT,
                color: selectedSize === s ? 'white' : NAVY
              }} data-id="element-2470">
                    {s}
                  </button>)}
              </div>
            </div>

            <CTAButton data-id="element-2471">PRE-ORDER NU - LEVERING HALF JULI</CTAButton>
            <div ref={trustpilotRef} className="trustpilot-widget mt-4" data-locale="nl-NL" data-template-id="5419b6ffb0d04a076446a9af" data-businessunit-id="65017f0060003ffc450ac45e" data-style-height="20px" data-style-width="100%" data-theme="light" data-id="element-2472">
              <a href="https://nl.trustpilot.com/review/stresslessdogs.nl" target="_blank" rel="noopener noreferrer" data-id="element-2473">
                Trustpilot
              </a>
            </div>
            <p className="text-sm text-center mt-3" style={{
            color: '#999999'
          }} data-id="element-2474">
              Pre-order tot 25 mei. Beperkte oplage - 100 sets.
            </p>
            <p className="text-sm text-center mt-3 leading-relaxed" style={{
            color: '#999999'
          }} data-id="element-2475">
              Let op. Dit is een "pre-order". Dit betekent dat onze harnassen &
              lijnen nu in productie zijn en je ze pas half juli ontvangt. Maar
              door nu al te bestellen steun je ons doel om het welzijn van
              honden in Nederland & Belgie te verbeteren. En als bedankje krijg
              jij een grote korting en gratis digitale cursus.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 2: THE PROBLEM */}
      <section className="py-16 md:py-24 px-4" style={{
      backgroundColor: MINT
    }} data-id="element-2476">
        <div className="max-w-[700px] mx-auto text-center" data-id="element-2477">
          <h2 className="font-bold text-3xl md:text-4xl mb-8 leading-tight" style={{
          color: NAVY
        }} data-id="element-2478">
            Wist je dat het verkeerde harnas je lieve hond pijn kan doen?
          </h2>
          <div className="space-y-5 text-lg" style={{
          color: NAVY
        }} data-id="element-2479">
            <p data-id="element-2480">
              Veel harnassen zien er goed uit, maar beperken je hond in zijn
              beweging. Ze drukken op de schouders, schuren bij de voorpoten, of
              geven druk op de keel. Je hond kan het je niet vertellen - maar
              hij voelt het bij elke stap.
            </p>
            <p data-id="element-2481">
              Het gevolg? Stress. Ongemak. Spanning aan de lijn. En soms zelfs
              gedragsproblemen die helemaal niet bij je hond horen, maar bij
              zijn materiaal.
            </p>
            <p className="font-bold text-xl md:text-2xl pt-3" style={{
            color: NAVY
          }} data-id="element-2482">
              Dat moet anders.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3: VIDEO */}
      <section className="bg-white py-16 md:py-24 px-4" data-id="element-2483">
        <div className="max-w-[800px] mx-auto text-center" data-id="element-2484">
          <h2 className="font-bold text-3xl md:text-4xl mb-4 leading-tight" style={{
          color: NAVY
        }} data-id="element-2485">
            Het begint met een goed tuig
          </h2>
          <p className="text-lg mb-10" style={{
          color: NAVY,
          opacity: 0.85
        }} data-id="element-2486">
            In deze video loopt Amee je door de anatomie van de hond en laat ze
            zien waarom het juiste tuig zo'n groot verschil maakt.
          </p>
          <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black" style={{
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }} data-id="element-2487">
            <iframe src="https://player.vimeo.com/video/500316327?badge=0&autopause=0&player_id=0&app_id=58479" allow="autoplay; fullscreen; picture-in-picture; clipboard-write" className="absolute top-0 left-0 w-full h-full" title="Het begint met een goed tuig" frameBorder="0" data-id="element-2488"></iframe>
          </div>
        </div>
      </section>

      {/* SECTION 4: AMEE'S STORY */}
      <section className="py-16 md:py-24 px-4" style={{
      backgroundColor: MINT
    }} data-id="element-2489">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 md:gap-14 items-center" data-id="element-2490">
          <div className="md:col-span-4 flex justify-center" data-id="element-2491">
            <img src="https://cdn.magicpatterns.com/uploads/vXmyJc5cCVvbLnBaXGCixn/682f298cbb713_AmeemetSophietje1.png" alt="Amee met haar hond Sophietje" className="w-56 h-56 md:w-72 md:h-72 rounded-full object-cover bg-white" style={{
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }} data-id="element-2492" />
          </div>
          <div className="md:col-span-8" data-id="element-2493">
            <h2 className="font-bold text-3xl md:text-4xl mb-6 leading-tight" style={{
            color: NAVY
          }} data-id="element-2494">
              Waarom ik dit harnas heb laten ontwikkelen
            </h2>
            <div className="space-y-5 text-lg pl-6 md:pl-8" style={{
            color: NAVY,
            borderLeft: `4px solid ${TEAL}`
          }} data-id="element-2495">
              <p data-id="element-2496">
                "Ik zag het te vaak. Lieve honden die onnodig gestrest waren,
                onnodig reactief, onnodig aan de lijn trokken. Niet omdat er
                iets mis was met de hond - maar omdat het materiaal niet deugde.
              </p>
              <p data-id="element-2497">
                Harnassen die de schouders blokkeren. Lijnen die te kort zijn om
                te snuffelen. Halsbanden die druk geven op de keel. Het deed me
                pijn om te zien.
              </p>
              <p data-id="element-2498">
                Samen met ons hele team van hondvriendelijke gedragsdeskundigen
                ben ik het anders gaan doen. We hebben een harnas ontwikkeld
                waarbij het welzijn van je hond volledig centraal staat. Geen
                compromissen.
              </p>
              <p data-id="element-2499">
                Ik gebruik het zelf, ons hele team gebruikt het. En nu is het
                eindelijk beschikbaar voor jou."
              </p>
            </div>
            <p className="mt-6 pl-6 md:pl-8 font-bold" style={{
            color: TEAL
          }} data-id="element-2500">
              Amee, oprichter Stressless Dogs & gedragsdeskundige
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5: FEATURES */}
      <section className="bg-white py-16 md:py-24 px-4" data-id="element-2501">
        <div className="max-w-6xl mx-auto" data-id="element-2502">
          <h2 className="font-bold text-3xl md:text-4xl text-center mb-14 md:mb-20 leading-tight max-w-3xl mx-auto" style={{
          color: NAVY
        }} data-id="element-2503">
            Ontworpen voor het welzijn van je hond. Niets anders.
          </h2>

          {[{
          title: 'Y-tuig ontwerp',
          body: 'Het Stressless Hondenharnas is een Y-tuig, zodat de schouders van je hond volledig vrij blijven. Geen druk op keel, gewrichten of luchtpijp. Je hond beweegt zoals de natuur het bedoeld heeft.',
          image: 'https://cdn.magicpatterns.com/uploads/brDAqt42EyvP3zKuTiRH7T/2fec549f084158399ab49791b65ad753.jpg',
          reverse: false
        }, {
          title: 'Geschikt voor iedere hond',
          body: 'Van Teckel tot Duitse Herder, van Franse Bulldog tot Border Terrier. Verstelbaar aan hals en buik, zodat het harnas zich aanpast aan het lichaam van jouw hond - niet andersom. Beschikbaar in 5 maten (XS t/m XL).',
          image: 'https://cdn.magicpatterns.com/uploads/uT4FhrbbD91cRCJrRg6Afa/dog_large_1_golden.jpg',
          reverse: true
        }, {
          title: 'De 3 meter lijn',
          body: 'De meeste lijnen zijn te kort. Onze lijn van 3 meter geeft je hond de ruimte om te snuffelen, te ontdekken en te ontspannen - terwijl je in contact blijft. Zo voelen jullie elkaar aan, zonder spanning.',
          image: 'https://cdn.magicpatterns.com/uploads/2B6nnwwgEyFX3RcpJW1fuM/72ee700432635495c54448ad503c21c0.jpg',
          reverse: false
        }].map((f, i) => <div key={i} className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center mb-14 md:mb-20 ${f.reverse ? 'md:[&>*:first-child]:order-2' : ''}`} data-id="element-2504">
              {f.image.startsWith('http') ? <img src={f.image} alt={f.title} className="w-full aspect-[4/3] object-cover rounded-2xl" style={{
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }} data-id="element-2505" /> : <ImagePlaceholder label={f.image} data-id="element-2506" />}
              <div data-id="element-2507">
                <h3 className="font-bold text-2xl md:text-3xl mb-4" style={{
              color: NAVY
            }} data-id="element-2508">
                  {f.title}
                </h3>
                <p className="text-lg" style={{
              color: NAVY
            }} data-id="element-2509">
                  {f.body}
                </p>
              </div>
            </div>)}

          {/* VIDEO BREAK: 3-meter leash */}
          <div className="max-w-[800px] mx-auto text-center mb-14 md:mb-20" data-id="element-2510">
            <span className="inline-block text-xs font-bold uppercase tracking-widest mb-4" style={{
            color: TEAL
          }} data-id="element-2511">
              Bekijk de video
            </span>
            <h3 className="font-bold text-2xl md:text-3xl mb-3 leading-tight" style={{
            color: NAVY
          }} data-id="element-2512">
              Waarom een lijn van 3 meter?
            </h3>
            <p className="text-lg mb-8" style={{
            color: NAVY,
            opacity: 0.85
          }} data-id="element-2513">
              In deze video leggen we uit waarom de juiste lijnlengte zo'n groot
              verschil maakt voor het welzijn van je hond.
            </p>
            <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black" style={{
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }} data-id="element-2514">
              <iframe src="https://player.vimeo.com/video/500316495?badge=0&autopause=0&player_id=0&app_id=58479" allow="autoplay; fullscreen; picture-in-picture; clipboard-write" className="absolute top-0 left-0 w-full h-full" title="Waarom een lijn van 3 meter?" frameBorder="0" data-id="element-2515"></iframe>
            </div>
          </div>

          {/* Feature 4 */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-center md:[&>*:first-child]:order-2" data-id="element-2516">
            <img src="https://cdn.magicpatterns.com/uploads/8Hjx3gN76fpffxCpSPDksA/1C2A3597.jpg" alt="Close-up van het Stressless Hondenharnas - reflecterende stiksels, gespen en gevoerd materiaal" className="w-full aspect-[4/3] object-cover rounded-2xl" style={{
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }} data-id="element-2517" />
            <div data-id="element-2518">
              <h3 className="font-bold text-2xl md:text-3xl mb-4" style={{
              color: NAVY
            }} data-id="element-2519">
                Gemaakt om lang mee te gaan
              </h3>
              <p className="text-lg" style={{
              color: NAVY
            }} data-id="element-2520">
                Stevig nylon dat reflecteert in het donker, aluminium gespen en
                zacht gevoerde banden. Geen schuurplekken, geen irritatie.
                Gebouwd voor dagelijks gebruik, bij elk weer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: BONUS MODULE */}
      <section className="py-16 md:py-24 px-4" style={{
      backgroundColor: MINT
    }} data-id="element-2521">
        <div className="max-w-3xl mx-auto" data-id="element-2522">
          <div className="bg-white rounded-2xl p-8 md:p-12 text-center" style={{
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }} data-id="element-2523">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold uppercase tracking-wider text-xs text-white mb-6" style={{
            backgroundColor: TEAL
          }} data-id="element-2524">
              <StarIcon className="w-4 h-4 fill-white" data-id="element-2525" />
              Bonus
            </div>
            <h2 className="font-bold text-2xl md:text-3xl mb-5 leading-tight" style={{
            color: NAVY
          }} data-id="element-2526">
              Inclusief Gratis Minicursus Lijnvoering: leer direct hoe je
              wandelt met een lijn van 3 meter
            </h2>
            <p className="text-lg mb-8" style={{
            color: NAVY
          }} data-id="element-2527">
              Bij je Stressless Wandelset ontvang je de complete Module
              Lijnvoering uit onze cursus "Stressless Wandelen met je Hond"
              (cursuswaarde €87). Zodat je niet alleen het beste materiaal hebt,
              maar ook precies weet hoe je ermee wandelt.
            </p>
            <ul className="space-y-3 text-left max-w-md mx-auto" data-id="element-2528">
              {lessons.map((l, i) => <li key={i} className="flex gap-3 items-start" data-id="element-2529">
                  <Check data-id="element-2530" />
                  <span className="text-base md:text-lg" style={{
                color: NAVY
              }} data-id="element-2531">
                    {l}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 7: SIZE TABLE */}
      <section className="py-16 md:py-24 px-4" style={{
      backgroundColor: MINT
    }} data-id="element-2532">
        <div className="max-w-3xl mx-auto" data-id="element-2533">
          <h2 className="font-bold text-3xl md:text-4xl mb-8 text-center leading-tight" style={{
          color: NAVY
        }} data-id="element-2534">
            Welke maat heeft jouw hond?
          </h2>

          {/* Measurement illustration */}
          <div className="max-w-[500px] mx-auto mb-8" data-id="element-2535">
            <img src="https://cdn.magicpatterns.com/uploads/hHyeKjZTf2frojXLNbAPgM/dog_measurement_guide_v2.png" alt="Hoe meet je de borst- en halsomvang van je hond" className="w-full h-auto rounded-2xl bg-white" style={{
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }} data-id="element-2536" />
          </div>

          <p className="text-lg mb-8 text-center" style={{
          color: NAVY
        }} data-id="element-2537">
            Meet de borstomvang van je hond vlak achter de voorpoten met een
            meetlint, met twee vingers tegen elkaar tussen het lint en de huid.
            Meet de hals halverwege de nek. Twijfel je tussen twee maten? Kies
            de grotere maat.
          </p>

          <div className="rounded-2xl overflow-hidden bg-white" style={{
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }} data-id="element-2538">
            <table className="w-full text-left" data-id="element-2539">
              <thead data-id="element-2540">
                <tr style={{
                backgroundColor: NAVY,
                color: 'white'
              }} data-id="element-2541">
                  <th className="py-3 px-4 font-bold text-sm" data-id="element-2542">Maat</th>
                  <th className="py-3 px-4 font-bold text-sm" data-id="element-2543">Borstomvang</th>
                  <th className="py-3 px-4 font-bold text-sm hidden sm:table-cell" data-id="element-2544">
                    Halsomvang
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-2545">
                {sizeTable.map((row, i) => <tr key={row.maat} style={{
                backgroundColor: i % 2 === 0 ? 'white' : MINT,
                color: NAVY
              }} data-id="element-2546">
                    <td className="py-3 px-4 font-bold" data-id="element-2547">{row.maat}</td>
                    <td className="py-3 px-4" data-id="element-2548">{row.borst}</td>
                    <td className="py-3 px-4 hidden sm:table-cell" data-id="element-2549">
                      {row.hals}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <p className="text-sm mt-4 text-center" style={{
          color: NAVY,
          opacity: 0.7
        }} data-id="element-2550">
            Verstelbaar aan hals en buik - past zich aan het lichaam van jouw
            hond aan.
          </p>
        </div>
      </section>

      {/* SECTION 8: PRE-SALE CTA */}
      <section className="py-16 md:py-24 px-4" style={{
      backgroundColor: NAVY
    }} data-id="element-2551">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 md:gap-14 items-start text-white" data-id="element-2552">
          {/* Product Gallery */}
          <div data-id="element-2553">
            <div className="w-full aspect-square rounded-2xl overflow-hidden mb-4" style={{
            backgroundColor: galleryImages[galleryIndex].bg === 'white' ? 'white' : MINT,
            boxShadow: '0 10px 40px rgba(0,0,0,0.25)'
          }} data-id="element-2554">
              <img src={galleryImages[galleryIndex].src} alt={galleryImages[galleryIndex].alt} className="w-full h-full object-cover" data-id="element-2555" />
            </div>
            <div className="flex justify-start gap-2 md:gap-3 flex-wrap" data-id="element-2556">
              {galleryImages.map((img, i) => <button key={i} onClick={() => setGalleryIndex(i)} aria-label={`Bekijk foto ${i + 1}`} className="w-14 h-14 md:w-16 md:h-16 rounded-xl overflow-hidden transition-all" style={{
              backgroundColor: img.bg === 'white' ? 'white' : MINT,
              border: galleryIndex === i ? `3px solid ${TEAL}` : '3px solid transparent',
              opacity: galleryIndex === i ? 1 : 0.65
            }} data-id="element-2557">
                  <img src={img.src} alt="" className="w-full h-full object-cover" data-id="element-2558" />
                </button>)}
            </div>
          </div>

          {/* Text + CTA */}
          <div className="text-center md:text-left" data-id="element-2559">
            <h2 className="font-bold text-3xl md:text-4xl mb-6 leading-tight" data-id="element-2560">
              Bestel nu met pre-order korting
            </h2>
            <p className="text-lg mb-6" style={{
            opacity: 0.9
          }} data-id="element-2561">
              Wees er als eerste bij. De Stressless Wandelset is voor het eerst
              beschikbaar - en als pre-order ontvang je de complete set voor{' '}
              <span className="font-bold" style={{
              color: CORAL
            }} data-id="element-2562">
                €44,95
              </span>{' '}
              in plaats van{' '}
              <span className="line-through opacity-70" data-id="element-2563">€69,90</span> als je de
              onderdelen later los koopt. Gratis verzending inbegrepen.
            </p>

            <div className="flex items-baseline justify-center md:justify-start gap-3 mb-8" data-id="element-2564">
              <span className="line-through text-xl opacity-70" data-id="element-2565">€69,90</span>
              <span className="font-bold text-4xl md:text-5xl" style={{
              color: CORAL
            }} data-id="element-2566">
                €44,95
              </span>
            </div>

            <p className="text-lg mb-8" style={{
            opacity: 0.9
          }} data-id="element-2567">
              Plus: de complete Module Lijnvoering uit de cursus "Stressless
              Wandelen" (cursuswaarde €87) - gratis inbegrepen.
            </p>

            <div className="inline-flex items-center gap-2 mb-8 px-5 py-3 rounded-full text-sm font-medium" style={{
            backgroundColor: 'rgba(255,255,255,0.1)'
          }} data-id="element-2568">
              <CalendarIcon className="w-4 h-4" style={{
              color: TEAL
            }} data-id="element-2569" />
              Verwachte levering: half juli 2026
            </div>

            <p className="text-sm mb-8" style={{
            opacity: 0.7
          }} data-id="element-2570">
              Annuleren kan kosteloos tot aan verzending. Retourneren op eigen
              kosten.
            </p>

            <CTAButton data-id="element-2571">PRE-ORDER NU - LEVERING HALF JULI</CTAButton>
            <p className="text-sm mt-4" style={{
            opacity: 0.7
          }} data-id="element-2572">
              Pre-order tot 25 mei. Beperkte oplage - 100 sets.
            </p>
            <p className="text-sm mt-3 leading-relaxed max-w-md mx-auto md:mx-0" style={{
            opacity: 0.7
          }} data-id="element-2573">
              Let op. Dit is een "pre-order". Dit betekent dat onze harnassen &
              lijnen nu in productie zijn en je ze pas half juli ontvangt. Maar
              door nu al te bestellen steun je ons doel om het welzijn van
              honden in Nederland & Belgie te verbeteren. En als bedankje krijg
              jij een grote korting en gratis digitale cursus.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 9: FAQ */}
      <section className="bg-white py-16 md:py-24 px-4" data-id="element-2574">
        <div className="max-w-[700px] mx-auto" data-id="element-2575">
          <h2 className="font-bold text-3xl md:text-4xl mb-10 text-center leading-tight" style={{
          color: NAVY
        }} data-id="element-2576">
            Veelgestelde vragen
          </h2>
          <div className="space-y-4" data-id="element-2577">
            {faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} data-id="element-2578" />)}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-4 text-center" style={{
      backgroundColor: MINT
    }} data-id="element-2579">
        <div className="max-w-3xl mx-auto" data-id="element-2580">
          <div className="inline-block px-6 py-3 rounded-full bg-white text-sm font-bold mb-5" style={{
          color: NAVY,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }} data-id="element-2581">
            Stressless Dogs
          </div>
          <p className="text-sm mb-2" style={{
          color: NAVY
        }} data-id="element-2582">
            Copyright 2026, Stressless Dogs
          </p>
          <p className="text-sm" style={{
          color: NAVY,
          opacity: 0.7
        }} data-id="element-2583">
            Vragen? Mail naar amee@stresslessdogs.nl
          </p>
        </div>
      </footer>

      {/* MOBILE STICKY BOTTOM BAR */}
      <div className={`md:hidden fixed bottom-0 left-0 right-0 bg-white transition-transform duration-300 ${showStickyBar ? 'translate-y-0' : 'translate-y-full'}`} style={{
      height: 70,
      padding: '12px 16px',
      boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
      zIndex: 999,
      fontFamily: '"Sen", sans-serif'
    }} data-id="element-2584">
        <div className="flex items-center justify-between gap-3 h-full" data-id="element-2585">
          <div className="flex flex-col leading-tight" data-id="element-2586">
            <span className="line-through text-xs" style={{
            color: '#999999'
          }} data-id="element-2587">
              €69,90
            </span>
            <span className="font-bold" style={{
            color: CORAL,
            fontSize: 18
          }} data-id="element-2588">
              €44,95
            </span>
          </div>
          <a href={CHECKOUT_URL} className="inline-block uppercase font-bold tracking-wide text-white px-6 py-3 text-sm whitespace-nowrap hover:opacity-95 transition-opacity" style={{
          backgroundColor: CORAL,
          borderRadius: 100,
          boxShadow: '0 4px 12px rgba(240,91,86,0.35)'
        }} data-id="element-2589">
            Bestel nu
          </a>
        </div>
      </div>
    </div>;
};